import { useEffect, useState } from 'react';
import { EmployeeModel } from '../../../models/employee';
import axios from '../../../lib/axios';
import {
  EMPLOYEE_DELETE,
  EMPLOYEE_FIND_MANY,
  FIND_ROLE_AND_PERMISSION,
} from '../../../lib/endpoints/employee';
import {
  OFFICER_PROFILE_DELETE,
  OFFICER_PROFILE_FIND_MANY,
} from '../../../lib/endpoints/officerProfile';
import { RoleAndPermissionModel } from '../../../models/roleAndPermission';
import { OfficerProfileModel } from '../../../models/officeProfile';
import { DEFAULT_ROWS_PER_PAGE } from '../../../config-global';

const useHrManageController = () => {
  const [data, setData] = useState<EmployeeModel[]>([]);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);
  const [roleValue, setRoleValue] = useState<any>(null!);
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedUserId, setSelectedUserId] = useState<string>(null!);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(null!);
  const [selectData, setSelectData] = useState<EmployeeModel>(null!);

  // ----------- Call Api -------------//
  const handleGetData = async () => {
    try {
      setLoadingData(true);
      const roleResponse = await axios.post(FIND_ROLE_AND_PERMISSION);
      const resRoleData: RoleAndPermissionModel[] = roleResponse?.data;
      const getRoleHr = resRoleData?.find((item: any) => item?.role_type === 'HR');

      const employeeResponse = await axios.post(`${EMPLOYEE_FIND_MANY}?limit=${100000}`, {
        roleId: getRoleHr?.id,
      });
      const officerResponse = await axios.get(`${OFFICER_PROFILE_FIND_MANY}?take=${100000}`);
      const officerProfileData = officerResponse.data;
      const roleType = resRoleData.reduce((obj: any, role: any) => {
        obj[role.id] = role.name;
        return obj;
      }, {});

      const updatedData = employeeResponse?.data.map((employee: any) => ({
        ...employee,
        role_type: roleType[employee.roleId],
      }));

      setData(
        updatedData?.map((data: EmployeeModel) => {
          const populateOfficer: EmployeeModel[] = officerProfileData.filter(
            (subData: OfficerProfileModel) => subData?.self_id === data?.id
          );
          return {
            ...data,
            profile: populateOfficer?.length > 0 ? populateOfficer[0] : null!,
          };
        })
      );
      setRoleValue(getRoleHr);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteAdminData = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await Promise.all([
        axios.delete(EMPLOYEE_DELETE + selectedUserId),
        axios.delete(OFFICER_PROFILE_DELETE + selectedProfileId),
      ]);
      await handleGetData();
      setOpenDeleteForm(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
      setOpenMenu(null);
    }
  };

  // ----------- Call Api End -------------//

  // ----------- Function -------------//

  const handleOpenMenu = (event: any, getId: string, profileId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedUserId(getId);
    setSelectedProfileId(profileId);
  };

  const handleOpenEditForm = () => {
    setOpenForm(true);
    const getData = data?.filter((data) => data?.id === selectedUserId)[0];
    setSelectData(getData);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setOpenMenu(null);
    setSelectData(null!);
  };

  // ----------- Function End-------------//

  // ----------- Filter --------------//

  const filterData = data.filter(
    (emp) =>
      emp?.username?.toLowerCase()?.includes(searchValue.toLocaleLowerCase()) ||
      emp?.profile?.fullName?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
      emp?.profile?.email?.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  const isSearchNotFound: boolean = !filterData.length && !!searchValue;
  const countPagination = Math.ceil(filterData.length / DEFAULT_ROWS_PER_PAGE);

  const rows = filterData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );

  // ----------- Filter End --------------//

  // ----------- useEffect --------------//

  useEffect(() => {
    handleGetData();
  }, []);

  // ----------- useEffect End --------------//

  return {
    loadingData,
    roleValue,
    rows,
    searchValue,
    isSearchNotFound,
    currentPage,
    handleChangePage: (_: any, newPage: any) => setCurrentPage(newPage),
    countPagination,
    filterData,
    openMenu,
    handleOpenMenu,
    handleCloseMenu: () => {
      setOpenMenu(null);
      setSelectedUserId('');
      setSelectedProfileId('');
    },
    openForm,
    handleOpenForm: () => {
      setOpenForm(true);
    },
    handleCloseForm,
    handleGetData,
    openDeleteForm,
    handleDeleteAdminData,
    loadingDeleteButton,
    handleCloseDeleteForm: () => setOpenDeleteForm(false),
    handleOpenDeleteForm: () => setOpenDeleteForm(true),
    handleOpenEditForm,
    handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    selectData,
  };
};
export default useHrManageController;
